<template>
  <div class="banners-manager">
    <section class="banners-hero">
      <div>
        <span class="banners-hero__eyebrow">Operação interna do Backoffice</span>
        <h3 class="banners-hero__title">Gestão de Avisos Chat</h3>
        <p class="banners-hero__subtitle">
          Centralize avisos críticos por tenant, com espelhamento automático entre Backoffice e Pedbot.
        </p>
      </div>

      <div class="banners-hero__actions" v-if="isOriginBackoffice && canSee('add')">
        <vs-button color="primary" @click="openCreate">
          Novo banner
        </vs-button>
      </div>
    </section>

    <section class="banners-filters">
      <div class="banners-filters__grid">
        <vs-input
          v-model="filters.search"
          class="w-full"
          label="Buscar por tenant, título ou mensagem"
          @keyup.enter="refreshList"
        />

        <div class="banners-filters__switch">
          <vs-switch v-model="filters.onlyInactive" @change="handleInactiveToggle" />
          <div class="banners-filters__switch-copy">
            <strong>Exibição</strong>
            <small>{{ filters.onlyInactive ? 'Somente inativos' : 'Somente ativos' }}</small>
          </div>
        </div>

        <div class="banners-filters__buttons">
          <vs-button type="border" color="dark" @click="resetFilters">
            Limpar
          </vs-button>
          <vs-button color="primary" @click="refreshList">
            Buscar
          </vs-button>
        </div>
      </div>
    </section>

    <section class="banners-table-card">
      <div class="banners-table-card__header mb-1">
        <div>
          <strong>{{ loading ? 'Carregando banners...' : `${pagination.totalItems} banner(s) ativos no total. ` }}</strong>
          <small>{{ filters.onlyInactive ? '  Listando apenas banners inativos.' : '   Listando banners ativos na página atual.' }}</small>
        </div>

        <div class="banners-pagination-control">
          <span>{{ paginationLabel }}</span>
          <vs-dropdown vs-trigger-click>
            <div class="banners-per-page">
              <span>{{ pagination.limit }} / página</span>
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item v-for="option in perPageOptions" :key="option" @click="changeLimit(option)">
                {{ option }}
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>
      </div>

      <vs-table :data="banners" noDataText="Nenhum banner encontrado." class="banners-table">
        <template slot="thead">
          <vs-th>Tenant / Cliente</vs-th>
          <vs-th>Título</vs-th>
          <vs-th>Fim da exibição</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Criação</vs-th>
          <vs-th>Ações</vs-th>
        </template>

        <template slot-scope="{data}">
          <tbody>
            <vs-tr v-for="(banner, index) in data" :key="`${banner.id}-${index}`">
              <vs-td>
                <div class="banners-cell">
                  <strong>{{ banner.tenant_name }}</strong>
                  <!---<small>Website ID {{ banner.website_id }}</small>-->
                </div>
              </vs-td>

              <vs-td>
                <div class="banners-cell">
                  <strong>{{ banner.title || 'Sem título' }}</strong>
                  <small class="truncate">{{ banner.message }}</small>
                </div>
              </vs-td>

              <vs-td>
                <div class="banners-cell">
                  <strong>{{ formatDate(banner.expires_at).full || 'Sem data final' }}</strong>
                  <small>Início: {{ formatDate(banner.start_at).full || 'Imediato' }}</small>
                </div>
              </vs-td>

              <vs-td>
                <vs-chip :color="bannerStatus(banner).color">
                  {{ bannerStatus(banner).label }}
                </vs-chip>
              </vs-td>

              <vs-td>
                <div class="banners-cell">
                  <strong>{{ formatDate(banner.created_at).full || '-' }}</strong>
                  <small>Atualizado: {{ formatDate(banner.updated_at).full || '-' }}</small>
                </div>
              </vs-td>

              <vs-td class="actionTD">
                <div class="banners-actions">
                  <vs-button
                    v-if="isOriginBackoffice && canSee('update')"
                    type="border"
                    size="small"
                    @click="openEdit(banner)"
                  >
                    Editar
                  </vs-button>

                  <vs-button
                    v-if="isOriginBackoffice && canSee('update')"
                    size="small"
                    :color="banner.is_active ? 'warning' : 'success'"
                    @click="toggleBannerStatus(banner)"
                  >
                    {{ banner.is_active ? 'Desativar' : 'Ativar' }}
                  </vs-button>

                  <vs-button
                    v-if="isOriginBackoffice && canSee('remove')"
                    type="border"
                    color="danger"
                    size="small"
                    @click="confirmDelete(banner)"
                  >
                    Excluir
                  </vs-button>
                </div>
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>

      <div class="banners-pagination">
        <vs-button type="border" color="dark" :disabled="pagination.page <= 1 || loading" @click="changePage(pagination.page - 1)">
          Página anterior
        </vs-button>

        <span>Página {{ pagination.page }} de {{ pagination.totalPages }}</span>

        <vs-button type="border" :disabled="pagination.page >= pagination.totalPages || loading" @click="changePage(pagination.page + 1)">
          Próxima página
        </vs-button>
      </div>
    </section>

    <vs-popup :active.sync="formDialog.active" :title="formDialog.mode === 'create' ? 'Novo banner' : 'Editar banner'" class="banners-popup">
      <div class="banners-form">
        <div class="banners-form__section">
          <span class="banners-form__eyebrow">Conteúdo do banner</span>

          <div class="vx-row">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <vs-input v-model="form.title" class="w-full" label="Título do aviso" />
            </div>

            <div class="vx-col w-full md:w-1/2 mb-4">
              <div class="banners-text-color-row">
                <vs-input v-model="form.textColor" class="w-full" label="Cor do texto" />
                <input v-model="form.textColorPicker" type="color" class="banners-color-input banners-color-input--inline" @input="syncTextColor" />
              </div>
            </div>

            <div class="vx-col w-full mb-4">
              <vs-textarea v-model="form.message" class="w-full" label="Mensagem do banner" />
            </div>

            <div class="vx-col w-full mb-4">
              <vs-input v-model="form.backgroundColor" class="w-full" label="Cor ou gradient de background" />
              <div class="banners-presets mt-2">
                <button
                  v-for="preset in backgroundPresets"
                  :key="preset.value"
                  type="button"
                  class="banners-preset"
                  :style="{ background: preset.value, color: preset.textColor }"
                  @click="applyBackgroundPreset(preset)"
                >
                  {{ preset.label }}
                </button>
              </div>

              <div class="banners-custom-background mt-4">
                <div class="banners-custom-background__header">
                  <strong>Customização avançada</strong>
                  <small>Monte uma cor sólida ou gradient fora dos presets sugeridos.</small>
                </div>

                <div class="banners-custom-background__row">
                  <div>
                    <label class="vs-input--label">Cor sólida customizada</label>
                    <div class="banners-solid-color-controls">
                      <input v-model="form.backgroundSolidColor" type="color" class="banners-color-input banners-color-input--solid" />
                      <vs-button type="border" @click="applySolidBackground">
                        Aplicar cor sólida
                      </vs-button>
                    </div>
                  </div>
                </div>

                <div class="banners-custom-background__row">
                  <div>
                    <label class="vs-input--label">Gradient customizado</label>
                    <div class="banners-gradient-controls">
                      <input v-model="form.gradientStart" type="color" class="banners-color-input" />
                      <input v-model="form.gradientEnd" type="color" class="banners-color-input" />
                      <vs-input v-model="form.gradientAngle" class="banners-angle-input" label="Ângulo" />
                    </div>
                  </div>

                  <vs-button type="border" @click="applyCustomGradient">
                    Aplicar gradient
                  </vs-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="banners-form__section">
          <span class="banners-form__eyebrow">Preview do cliente final</span>
          <div class="banners-preview" :style="previewStyle">
            <strong v-if="normalizedTitle">{{ normalizedTitle }}</strong>
            <div class="banners-preview__message" v-html="previewMessageHtml"></div>
          </div>
        </div>

        <div class="banners-form__section">
          <span class="banners-form__eyebrow">Clientes / Tenants</span>

          <div v-if="formDialog.mode === 'edit'" class="banners-locked-target">
            <strong>{{ form.editTarget ? form.editTarget.tenant_name : '-' }}</strong>
            <small>O tenant do banner não pode ser alterado durante a edição.</small>
          </div>

          <div v-else>
            <div class="banners-mode-grid">
              <vs-radio v-model="form.targetMode" vs-name="bannerTargetMode" vs-value="single">Tenant único</vs-radio>
              <vs-radio v-model="form.targetMode" vs-name="bannerTargetMode" vs-value="multiple">Seleção múltipla</vs-radio>
              <vs-radio v-model="form.targetMode" vs-name="bannerTargetMode" vs-value="all">Todos os tenants</vs-radio>
              <vs-radio v-model="form.targetMode" vs-name="bannerTargetMode" vs-value="file">CSV / planilha</vs-radio>
            </div>

            <div v-if="form.targetMode === 'single'" class="mt-4">
              <label class="vs-input--label">Tenant alvo</label>
              <v-select
                v-model="form.singleTenant"
                :options="tenantOptions"
                label="label"
                :reduce="item => item.tenantName"
                placeholder="Selecione um tenant"
              />
            </div>

            <div v-if="form.targetMode === 'multiple'" class="mt-4">
              <label class="vs-input--label">Tenants selecionados</label>
              <v-select
                v-model="form.multipleTenants"
                :options="tenantOptions"
                label="label"
                :reduce="item => item.tenantName"
                multiple
                placeholder="Selecione um ou mais tenants"
              />
            </div>

            <div v-if="form.targetMode === 'all'" class="banners-selection-summary mt-4">
              <strong>{{ activeTenants.length }} tenants serão afetados</strong>
              <small>Todos os tenants ativos deste ambiente receberão o banner.</small>
            </div>

            <div v-if="form.targetMode === 'file'" class="mt-4">
              <div class="banners-file-box">
                <input ref="bannerFileInput" type="file" accept=".csv,.xlsx,.xls" class="hidden" @change="handleTenantFile">
                <vs-button type="border" @click="$refs.bannerFileInput.click()">
                  Importar CSV / planilha
                </vs-button>
                <small>A primeira coluna deve conter apenas o nome do tenant.</small>
              </div>

              <div class="banners-selection-summary" v-if="form.importSummary.matched.length || form.importSummary.unmatched.length">
                <strong>{{ form.importSummary.matched.length }} tenant(s) reconhecido(s)</strong>
                <small v-if="form.importSummary.unmatched.length">
                  Não encontrados: {{ form.importSummary.unmatched.join(', ') }}
                </small>
              </div>
            </div>

            <div class="banners-selection-summary mt-4" v-if="selectedTargetTenants.length">
              <strong>{{ selectedTargetTenants.length }} tenant(s) no lote</strong>
              <small>{{ selectedTargetTenants.map(item => item.label).join(', ') }}</small>
            </div>
          </div>
        </div>

        <div class="banners-form__section">
          <span class="banners-form__eyebrow">Período de exibição</span>

          <div class="vx-row">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="vs-input--label">Data de início da exibição:</label>
              <flat-pickr
                v-model="form.startAt"
                class="w-full"
                :config="dateTimePickerConfig"
                placeholder="A partir de agora"
              />
              <vs-button type="border" size="small" class="mt-2" @click="form.startAt = ''">
                Início imediato
              </vs-button>
            </div>

            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="vs-input--label">Data final da exibição:</label>
              <flat-pickr
                v-model="form.expiresAt"
                class="w-full"
                :config="dateTimePickerConfig"
                placeholder="Sem data final"
              />
              <vs-button type="border" size="small" class="mt-2" @click="form.expiresAt = ''">
                Sem expiração
              </vs-button>
            </div>
          </div>
        </div>

        <div class="banners-form__footer">
          <vs-button type="border" color="dark" @click="closeForm">
            Cancelar
          </vs-button>
          <vs-button color="primary" :disabled="saving" @click="submitForm">
            {{ saving ? 'Salvando...' : formDialog.mode === 'create' ? 'Criar banner(s)' : 'Salvar alterações' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import XLSX from 'xlsx';
import vSelect from 'vue-select';
import flatPickr from 'vue-flatpickr-component';
import { mapGetters } from 'vuex';
import 'flatpickr/dist/flatpickr.css';
import 'vue-select/dist/vue-select.css';
import { Portuguese } from 'flatpickr/dist/l10n/pt.js';
import bannersRequest from '@/http/requests/banners';
import tenantsRequest from '@/http/requests/tenants';
import { filterTenantsByHost, formatDate, formatTenantLabel, hasPermissionBackOffice } from '@/utils';

const defaultForm = () => ({
  title: '',
  message: '',
  textColor: '#ffffff',
  textColorPicker: '#ffffff',
  backgroundColor: 'linear-gradient(135deg, #d62828 0%, #8f1d1d 100%)',
  backgroundSolidColor: '#374151',
  gradientStart: '#d62828',
  gradientEnd: '#8f1d1d',
  gradientAngle: '135',
  startAt: '',
  expiresAt: '',
  targetMode: 'single',
  singleTenant: null,
  multipleTenants: [],
  importSummary: {
    matched: [],
    unmatched: []
  },
  editTarget: null
});

export default {
  name: 'banners-list',
  components: {
    flatPickr,
    vSelect
  },
  data() {
    return {
      loading: false,
      saving: false,
      banners: [],
      tenants: [],
      filters: {
        search: '',
        onlyInactive: false
      },
      pagination: {
        page: 1,
        limit: 4,
        totalPages: 1,
        totalItems: 0
      },
      perPageOptions: [4, 10, 20, 30, 50],
      formDialog: {
        active: false,
        mode: 'create'
      },
      form: defaultForm(),
      backgroundPresets: [
        { label: 'Crítico', value: 'linear-gradient(135deg, #d62828 0%, #8f1d1d 100%)', textColor: '#ffffff' },
        { label: 'Atenção', value: 'linear-gradient(135deg, #f77f00 0%, #fcbf49 100%)', textColor: '#1f2937' },
        { label: 'Operação', value: 'linear-gradient(135deg, #003049 0%, #1d4e89 100%)', textColor: '#ffffff' },
        { label: 'Neutro', value: '#374151', textColor: '#ffffff' }
      ],
      dateTimePickerConfig: {
        enableTime: true,
        noCalendar: false,
        dateFormat: 'd/m/Y H:i',
        time_24hr: true,
        locale: Portuguese
      }
    };
  },
  computed: {
    ...mapGetters(['token']),
    isOriginBackoffice() {
      return process.env.VUE_APP_ORIGIN_BACKOFFICE && process.env.VUE_APP_ORIGIN_BACKOFFICE === 'true';
    },
    tenantOptions() {
      return this.activeTenants.map(tenant => ({
        label: formatTenantLabel(tenant),
        tenantName: formatTenantLabel(tenant),
        websiteId: tenant.website_id || tenant.id,
        raw: tenant
      }));
    },
    activeTenants() {
      return filterTenantsByHost(this.tenants).filter(item => !item.deleted_at);
    },
    selectedTargetTenants() {
      if (this.formDialog.mode === 'edit') {
        return this.form.editTarget ? [{
          label: this.form.editTarget.tenant_name,
          tenantName: this.form.editTarget.tenant_name,
          websiteId: this.form.editTarget.website_id
        }] : [];
      }

      if (this.form.targetMode === 'single') {
        return this.form.singleTenant ? [this.findTenantOptionByName(this.form.singleTenant)].filter(Boolean) : [];
      }

      if (this.form.targetMode === 'multiple') {
        return (this.form.multipleTenants || [])
          .map(name => this.findTenantOptionByName(name))
          .filter(Boolean);
      }

      if (this.form.targetMode === 'all') {
        return this.tenantOptions;
      }

      return this.form.importSummary.matched || [];
    },
    previewStyle() {
      return {
        color: this.form.textColor || '#ffffff',
        background: this.form.backgroundColor || '#374151'
      };
    },
    previewMessageHtml() {
      return this.formatPreviewMessage(
        this.form.message || 'Sua mensagem aparecerá aqui assim que o conteúdo for preenchido.'
      );
    },
    normalizedTitle() {
      return this.form.title && this.form.title.trim() ? this.form.title.trim() : '';
    },
    paginationLabel() {
      if (!this.pagination.totalItems) {
        return '0 resultados';
      }

      const start = ((this.pagination.page - 1) * this.pagination.limit) + 1;
      const end = Math.min(this.pagination.page * this.pagination.limit, this.pagination.totalItems);
      return `${start} - ${end} de ${this.pagination.totalItems}`;
    }
  },
  async mounted() {
    await Promise.all([
      this.loadTenants(),
      this.loadBanners()
    ]);
  },
  methods: {
    formatDate,
    canSee(word = '') {
      if (!word) {
        return hasPermissionBackOffice(this.$route.name);
      }

      return hasPermissionBackOffice(`${this.$route.name}-${word}`);
    },
    bannerStatus(banner) {
      return banner.is_active
        ? { label: 'Ativo', color: 'success' }
        : { label: 'Inativo', color: 'danger' };
    },
    async loadTenants() {
      try {
        const items = await tenantsRequest.getAllTenants(this.token, true);
        this.tenants = Array.isArray(items) ? items : [];
      } catch (error) {
        this.tenants = [];
        this.notify('danger', 'Erro ao carregar tenants.', this.extractErrorMessage(error));
      }
    },
    async loadBanners() {
      this.loading = true;
      try {
        const response = await bannersRequest.list(this.token, {
          page: this.pagination.page,
          limit: this.pagination.limit,
          search: this.filters.search || undefined,
          onlyActive: !this.filters.onlyInactive,
          onlyInactive: this.filters.onlyInactive
        });

        const payload = response.data || {};
        this.banners = payload.items || [];
        this.pagination.totalPages = payload.total_pages || 1;
        this.pagination.totalItems = payload.total_items || 0;
      } catch (error) {
        this.banners = [];
        this.pagination.totalPages = 1;
        this.pagination.totalItems = 0;
        this.notify('danger', 'Erro ao carregar banners.', this.extractErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    refreshList() {
      this.pagination.page = 1;
      return this.loadBanners();
    },
    resetFilters() {
      this.filters.search = '';
      this.filters.onlyInactive = false;
      return this.refreshList();
    },
    handleInactiveToggle() {
      return this.refreshList();
    },
    changePage(page) {
      this.pagination.page = page;
      return this.loadBanners();
    },
    changeLimit(limit) {
      this.pagination.limit = limit;
      this.pagination.page = 1;
      return this.loadBanners();
    },
    openCreate() {
      this.formDialog.mode = 'create';
      this.formDialog.active = true;
      this.form = defaultForm();
    },
    openEdit(banner) {
      this.formDialog.mode = 'edit';
      this.formDialog.active = true;
      this.form = {
        ...defaultForm(),
        title: banner.title || '',
        message: banner.message || '',
        textColor: banner.text_color || '#ffffff',
        textColorPicker: this.normalizeColorPickerValue(banner.text_color),
        backgroundColor: banner.background_color || '#374151',
        backgroundSolidColor: '#374151',
        gradientStart: '#d62828',
        gradientEnd: '#8f1d1d',
        gradientAngle: '135',
        startAt: this.formatDateForPickerValue(banner.start_at),
        expiresAt: this.formatDateForPickerValue(banner.expires_at),
        editTarget: banner
      };
    },
    findTenantOptionByName(name) {
      return this.tenantOptions.find(item => item.tenantName === name) || null;
    },
    closeForm() {
      this.formDialog.active = false;
      this.form = defaultForm();
    },
    syncTextColor() {
      this.form.textColor = this.form.textColorPicker;
    },
    applyBackgroundPreset(preset) {
      this.form.backgroundColor = preset.value;
      if (preset.textColor) {
        this.form.textColor = preset.textColor;
        this.form.textColorPicker = this.normalizeColorPickerValue(preset.textColor);
      }
    },
    applySolidBackground() {
      this.form.backgroundColor = this.form.backgroundSolidColor;
    },
    applyCustomGradient() {
      const angle = Number(this.form.gradientAngle || 135);
      this.form.backgroundColor = `linear-gradient(${angle}deg, ${this.form.gradientStart} 0%, ${this.form.gradientEnd} 100%)`;
    },
    formatDateForPickerValue(value) {
      if (!value) {
        return '';
      }

      const formatted = formatDate(value);
      return formatted.full ? `${formatted.day}/${formatted.month}/${formatted.year} ${formatted.hour}:${formatted.minute}` : '';
    },
    normalizeColorPickerValue(value) {
      if (typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)) {
        return value;
      }

      return '#ffffff';
    },
    async handleTenantFile(event) {
      const [file] = event.target.files || [];
      if (!file) {
        return;
      }

      try {
        const buffer = await this.readFileAsArrayBuffer(file);
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        const tenantNames = rows
          .map(row => Array.isArray(row) ? row[0] : null)
          .filter(Boolean)
          .map(value => String(value).trim())
          .filter(Boolean);

        const normalizedAvailable = new Map(
          this.tenantOptions.map(item => [item.tenantName.toLowerCase(), item])
        );

        const matched = [];
        const unmatched = [];
        tenantNames.forEach((name, index) => {
          const normalized = name.toLowerCase();
          const found = normalizedAvailable.get(normalized);
          const isHeader = ['tenant', 'tenant_name', 'tenantname', 'fqdn', 'nome', 'nome_tenant'].includes(normalized);

          if (found) {
            if (!matched.find(item => item.tenantName === found.tenantName)) {
              matched.push(found);
            }
            return;
          }

          if (index === 0 && isHeader) {
            return;
          }

          unmatched.push(name);
        });

        this.form.importSummary = { matched, unmatched };

        if (!matched.length) {
          this.notify('warning', 'Nenhum tenant reconhecido.', 'Verifique os nomes da primeira coluna da planilha.');
        }
      } catch (error) {
        this.notify('danger', 'Erro ao processar arquivo.', this.extractErrorMessage(error));
      } finally {
        if (this.$refs.bannerFileInput) {
          this.$refs.bannerFileInput.value = null;
        }
      }
    },
    validateForm() {
      if (!this.form.title || !this.form.title.trim()) {
        this.notify('warning', 'Título obrigatório.', 'Preencha o título do banner.');
        return false;
      }

      if (!this.form.message || !this.form.message.trim()) {
        this.notify('warning', 'Mensagem obrigatória.', 'Preencha a mensagem do banner.');
        return false;
      }

      if (!this.form.textColor || !this.form.backgroundColor) {
        this.notify('warning', 'Cores obrigatórias.', 'Defina a cor do texto e o background do banner.');
        return false;
      }

      if (this.form.startAt && this.form.expiresAt && this.parsePickerDate(this.form.startAt) > this.parsePickerDate(this.form.expiresAt)) {
        this.notify('warning', 'Período inválido.', 'A data inicial não pode ser maior que a data final.');
        return false;
      }

      if (!this.selectedTargetTenants.length) {
        this.notify('warning', 'Seleção obrigatória.', 'Selecione ao menos um tenant para o banner.');
        return false;
      }

      return true;
    },
    buildPayload(target) {
      return {
        websiteId: target.websiteId,
        tenantName: target.tenantName,
        title: this.normalizedTitle || null,
        message: this.form.message.trim(),
        textColor: this.form.textColor,
        backgroundColor: this.form.backgroundColor,
        startAt: this.form.startAt || null,
        expiresAt: this.form.expiresAt || null,
        isActive: true
      };
    },
    buildBatchPayload() {
      return {
        title: this.normalizedTitle || null,
        message: this.form.message.trim(),
        textColor: this.form.textColor,
        backgroundColor: this.form.backgroundColor,
        startAt: this.form.startAt || null,
        expiresAt: this.form.expiresAt || null,
        isActive: true,
        targets: this.selectedTargetTenants.map(target => ({
          websiteId: target.websiteId,
          tenantName: target.tenantName
        }))
      };
    },
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        if (this.formDialog.mode === 'edit' && this.form.editTarget) {
          await bannersRequest.update(this.token, this.form.editTarget.id, {
            title: this.normalizedTitle || null,
            message: this.form.message.trim(),
            textColor: this.form.textColor,
            backgroundColor: this.form.backgroundColor,
            startAt: this.form.startAt || null,
            expiresAt: this.form.expiresAt || null
          });

          this.notify('success', 'Banner atualizado.', 'As alterações foram refletidas no Backoffice e no Pedbot.');
        } else {
          const response = await bannersRequest.create(this.token, this.buildBatchPayload());
          const summary = (((response || {}).data || {}).summary) || {};
          const successCount = Number(summary.success || 0);
          const failureCount = Number(summary.failed || 0);

          if (failureCount) {
            this.notify(
              'warning',
              'Criação parcial concluída.',
              `${successCount} banner(s) criado(s) com sucesso e ${failureCount} falharam.`
            );
          } else {
            this.notify('success', 'Banners criados.', `${successCount} banner(s) criado(s) com sucesso.`);
          }
        }

        this.closeForm();
        await this.refreshList();
      } catch (error) {
        this.notify('danger', 'Erro ao salvar banner.', this.extractErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    async toggleBannerStatus(banner) {
      try {
        await bannersRequest.update(this.token, banner.id, {
          isActive: !banner.is_active
        });

        this.notify(
          'success',
          banner.is_active ? 'Banner desativado.' : 'Banner ativado.',
          'A alteração foi refletida no Backoffice e no Pedbot.'
        );

        await this.loadBanners();
      } catch (error) {
        this.notify('danger', 'Erro ao alterar status.', this.extractErrorMessage(error));
      }
    },
    confirmDelete(banner) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Excluir banner',
        text: `Deseja remover o banner de ${banner.tenant_name}?`,
        accept: () => this.deleteBanner(banner),
        acceptText: 'Sim',
        cancelText: 'Não'
      });
    },
    async deleteBanner(banner) {
      try {
        await bannersRequest.remove(this.token, banner.id);
        this.notify('success', 'Banner removido.', 'O banner foi removido do Backoffice e do Pedbot.');
        await this.loadBanners();
      } catch (error) {
        this.notify('danger', 'Erro ao remover banner.', this.extractErrorMessage(error));
      }
    },
    notify(color, title, text) {
      this.$vs.notify({
        color,
        title,
        text
      });
    },
    parsePickerDate(value) {
      if (!value || typeof value !== 'string') {
        return null;
      }

      const [datePart, timePart = '00:00'] = value.split(' ');
      const [day, month, year] = datePart.split('/');
      const [hour = '00', minute = '00'] = timePart.split(':');
      return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), 0).getTime();
    },
    formatPreviewMessage(value) {
      const escaped = String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      return escaped
        .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
        .replace(/_(.*?)_/g, '<em>$1</em>')
        .replace(/~(.*?)~/g, '<s>$1</s>')
        .replace(/\n/g, '<br>');
    },
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },
    extractErrorMessage(error) {
      if (error && error.response && error.response.data) {
        if (error.response.data.failed && error.response.data.failed.length) {
          const firstFailure = error.response.data.failed[0];
          return firstFailure.message || error.response.data.message || 'Falha inesperada.';
        }

        if (error.response.data.message) {
          return error.response.data.message;
        }

        if (typeof error.response.data === 'string') {
          return error.response.data;
        }
      }

      return (error && error.message) || 'Falha inesperada.';
    }
  }
};
</script>

<style lang="scss" scoped>
.banners-manager {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.banners-hero,
.banners-filters,
.banners-table-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px;
  padding: 1.5rem;
}

.banners-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
}

.banners-hero__eyebrow,
.banners-form__eyebrow {
  display: inline-block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #b45309;
  margin-bottom: 0.4rem;
}

.banners-hero__title {
  margin: 0;
  font-size: 1.75rem;
  color: #0f172a;
}

.banners-hero__subtitle {
  margin: 0.5rem 0 0;
  color: #475569;
  max-width: 760px;
}

.banners-filters__grid {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto auto;
  gap: 1rem;
  align-items: end;
}

.banners-filters__buttons {
  display: flex;
  gap: 0.75rem;
}

.banners-filters__switch {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 42px;
}

.banners-filters__switch-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.banners-filters__switch-copy strong {
  font-size: 0.85rem;
  color: #0f172a;
}

.banners-filters__switch-copy small {
  color: #64748b;
}

.banners-table-card__header,
.banners-pagination,
.banners-hero__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.banners-pagination-control {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.banners-per-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 999px;
  padding: 0.75rem 1rem;
  cursor: pointer;
}

.banners-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.banners-cell small,
.banners-table-card__header small,
.banners-selection-summary small,
.banners-locked-target small {
  color: #64748b;
}

.banners-actions,
.banners-form__footer,
.banners-presets,
.banners-mode-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.banners-pagination {
  margin-top: 1rem;
}

.banners-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.banners-form__section {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  padding: 1rem;
}

.banners-text-color-row {
  display: flex;
  align-items: end;
  gap: 0.75rem;
}

.banners-text-color-row > .w-full {
  flex: 1;
}

.banners-preview {
  border-radius: 14px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 96px;
}

.banners-preview__message {
  white-space: normal;
  line-height: 1.55;
}

.banners-preset {
  border: none;
  border-radius: 999px;
  padding: 0.55rem 1rem;
  cursor: pointer;
  font-size: 0.85rem;
}

.banners-file-box,
.banners-selection-summary,
.banners-locked-target {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.banners-custom-background {
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
  margin-top: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  background: #fbfcfe;
}

.banners-custom-background__header {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.banners-custom-background__header strong {
  color: #0f172a;
  font-size: 0.92rem;
}

.banners-custom-background__header small {
  color: #64748b;
  line-height: 1.4;
}

.banners-custom-background__row {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding-top: 0.35rem;
}

.banners-custom-background__row + .banners-custom-background__row {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  padding-top: 1.15rem;
}

.banners-solid-color-controls {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-top: 0.45rem;
}

.banners-gradient-controls {
  display: flex;
  gap: 0.75rem;
  align-items: end;
  flex-wrap: wrap;
}

.banners-angle-input {
  min-width: 110px;
}

.banners-color-input {
  width: 54px;
  height: 38px;
  border: none;
  background: transparent;
  padding: 0;
  margin-top: 0.6rem;
  cursor: pointer;
}

.banners-color-input--inline {
  flex: 0 0 54px;
  margin-top: 0;
  margin-bottom: 2px;
}

.banners-color-input--solid {
  margin-top: 0;
}

.truncate {
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 991px) {
  .banners-filters__grid {
    grid-template-columns: 1fr;
  }

  .banners-hero,
  .banners-table-card__header,
  .banners-pagination {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>